import { Fragment, useMemo, useState } from "react";
import type { PortfolioPosition, PortfolioTrade } from "../../types/portfolio";
import { StatusMessage } from "../ui";
import { markFormatter, entryPriceFormatter, getEntryPrice } from "./portfolioUtils";
import { cn } from "../../lib/utils";
import type { TutorialSlide } from "../home/HomeTutorialModal";
import { TeamCodeBadge } from "../ui/TeamCodeBadge";
import { TEAM_IDENTITY_BADGE_SIZE, TEAM_IDENTITY_NAME_CLASS } from "../ui/teamIdentityScale";
import { getWorldCupFlagCountryCode } from "@sportex100/shared/utils/worldCupFlags";
import { isSoccerTradingCompetition } from "@sportex100/shared/utils/competitionIdentity.ts";
import {
  buildPositionTradeColumns,
  groupPositionTradesByTeamCode,
} from "@sportex100/shared/utils/portfolioPositionTrades.ts";
import { useLocalization } from "../../lib/i18n";

export const positionsHowToSlides: TutorialSlide[] = [
  {
    title: "Side: LONG vs SHORT",
    body: `Your **Side** tells you which direction you're betting.

**LONG** means you *bought* shares - you win points if the team's value goes UP by winning more games and progressing further in the playoffs.

**SHORT** means you *sold* shares - you win points if the team gets eliminated early.

Think of it like this: LONG = cheering for the team, SHORT = betting against them.`,
  },
  {
    title: "Quantity (Qty)",
    body: `**Qty** is simply how many shares you own.

The more shares you have, the bigger your potential profit (or loss). Each share moves with the team's value.

Example: If you own **5** shares and the price moves by 10 points, your position changes by **50** points (5 x 10).`,
  },
  {
    title: "Average Price",
    body: `**Avg Price** is your average purchase/sale price per share.

This is calculated automatically when you make multiple trades. If you bought 2 shares at 30 and 3 shares at 40, your avg price becomes **36** ((2x30 + 3x40) / 5).

Your profit or loss is measured from this average price.`,
  },
  {
    title: "Mark (Current Value)",
    body: `**Mark** is the current fair value of the team.

This is what the market thinks each share is worth *right now*. It updates based on recent trades and the order book.

Compare the Mark to your Avg Price to see if you're winning or losing.`,
  },
  {
    title: "Trading P&L",
    body: `**Trading P&L** shows your profit or loss from trading.

Formula: **(Mark - Avg Price) x Qty** for LONG positions, or **(Avg Price - Mark) x Qty** for SHORT.

This includes both open positions (mark-to-market) and closed positions (locked-in gains/losses from trading). When a team is **eliminated** or **wins the championship**, positions settle and the P&L becomes "realized".`,
  },
];

type PositionsSectionProps = {
  positions: PortfolioPosition[];
  trades: PortfolioTrade[];
  competitionCode: string;
  quantityFormatter: Intl.NumberFormat;
  numberFormatter: Intl.NumberFormat;
  entryPriceFormatter?: Intl.NumberFormat;
};

export function PositionsHowToButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="text-foreground-muted p-1 rounded hover:text-brand transition"
      aria-label="How to understand positions"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-5 w-5"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M12 16v-4" />
        <path d="M12 8h.01" />
      </svg>
    </button>
  );
}

export function PositionsSection({
  positions,
  trades,
  competitionCode,
  quantityFormatter,
  numberFormatter,
  entryPriceFormatter: entryFormatter = entryPriceFormatter,
}: PositionsSectionProps) {
  const { t } = useLocalization();
  const [expandedPositionId, setExpandedPositionId] = useState<number | null>(null);
  const positionTradesByTeamCode = useMemo(() => groupPositionTradesByTeamCode(trades), [trades]);
  const toggleExpandedPosition = (positionId: number) => {
    setExpandedPositionId((current) => (current === positionId ? null : positionId));
  };

  if (positions.length === 0) {
    return (
      <div className="p-3">
        <StatusMessage>No positions yet.</StatusMessage>
      </div>
    );
  }

  return (
    <div className="p-2">
      <div>
        <table className="hidden w-full table-fixed">
          <thead className="border-b border-white/10 bg-black/25">
            <tr className="h-10 text-[11px] font-semibold uppercase tracking-[0.14em] text-foreground-muted">
              <th className="w-[200px] px-3 text-left">Team</th>
              <th className="w-[80px] px-2 text-left">Direction</th>
              <th className="w-[80px] px-2 text-left">Qty</th>
              <th className="w-[100px] px-2 text-left">Entry</th>
              <th className="w-[80px] px-2 text-left">Mark</th>
              <th className="w-[100px] px-3 text-right">P&amp;L</th>
            </tr>
          </thead>
          <tbody>
            {positions.map((position) => {
              const isExpanded = expandedPositionId === position.position_id;
              const entryValue = getEntryPrice(position);
              const teamName =
                (position as PortfolioPosition & { team_name?: string }).team_name ?? position.team_code;
              const positionPnl = getPositionTradingPnl(position);
              const pnl = formatPnl(positionPnl, numberFormatter);
              const sideLabel = getPositionSideLabel(position, t);
              const sideTone = getPositionSideTone(position);
              const worldCupFlagCountryCode =
                isSoccerTradingCompetition(competitionCode) ? getWorldCupFlagCountryCode(position.team_code) : null;
              const { buyTrades, sellTrades, buySummary, sellSummary, pnlBreakdown } = buildPositionTradeColumns({
                positionTeamCode: position.team_code,
                trades: positionTradesByTeamCode.get(position.team_code.trim().toUpperCase()) ?? [],
                totalTradingPnl: position.position_realized_pnl + (position.unrealized_pnl ?? 0),
                realizedPnl: position.position_realized_pnl,
                unrealizedPnl: position.unrealized_pnl,
              });

              return (
                <Fragment key={position.position_id}>
                  <tr
                    className="h-12 border-b border-white/[0.08] text-sm transition-colors last:border-b-0 hover:bg-white/[0.03]"
                  >
                    <td className="px-3">
                      <button
                        type="button"
                        onClick={() => toggleExpandedPosition(position.position_id)}
                        aria-expanded={isExpanded}
                        className="flex min-w-0 items-center gap-2.5 text-left"
                      >
                        <span className="font-display text-sm text-foreground-muted" aria-hidden="true">
                          {isExpanded ? "▴" : "▾"}
                        </span>
                        <TeamCodeBadge
                          code={position.team_code}
                          size={
                            worldCupFlagCountryCode
                              ? TEAM_IDENTITY_BADGE_SIZE.portfolio.worldCupTableBadge
                              : TEAM_IDENTITY_BADGE_SIZE.portfolio.tableBadge
                          }
                          primaryColor={position.team_primary_color ?? null}
                          secondaryColor={position.team_secondary_color ?? null}
                          variant={worldCupFlagCountryCode ? "worldCupFlag" : undefined}
                          flagCountryCode={worldCupFlagCountryCode}
                        />
                        <span className="truncate font-semibold text-foreground">{teamName}</span>
                      </button>
                    </td>
                    <td className="px-2">
                      <span
                        className={cn(
                          "inline-flex min-w-[66px] items-center justify-center rounded-pill px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.1em]",
                          sideTone,
                        )}
                      >
                        {sideLabel}
                      </span>
                    </td>
                    <td className="px-2 text-foreground">{quantityFormatter.format(Math.abs(position.quantity))}</td>
                    <td className="px-2 text-foreground">
                      {entryValue != null ? entryFormatter.format(entryValue) : "—"}
                    </td>
                    <td className="px-2 text-foreground">{markFormatter.format(position.current_score)}</td>
                    <td className={cn("px-3 text-right font-semibold", pnl.tone)}>
                      {pnl.value}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr key={`${position.position_id}-details`} className="border-b border-white/[0.08] bg-background-surface/35">
                      <td colSpan={6} className="px-3 py-3">
                        <PositionTradeDetails
                          buyTrades={buyTrades}
                          sellTrades={sellTrades}
                          buySummary={buySummary}
                          sellSummary={sellSummary}
                          pnlBreakdown={pnlBreakdown}
                          numberFormatter={numberFormatter}
                          entryFormatter={entryFormatter}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>

        <div className="w-full space-y-3">
          {positions.map((position) => {
            const isExpanded = expandedPositionId === position.position_id;
            const entryValue = getEntryPrice(position);
            const teamName =
              (position as PortfolioPosition & { team_name?: string }).team_name ?? position.team_code;
            const positionPnl = getPositionTradingPnl(position);
            const pnl = formatPnl(positionPnl, numberFormatter);
            const sideLabel = getPositionSideLabel(position, t);
            const sideTone = getPositionSideTone(position);
            const worldCupFlagCountryCode =
              isSoccerTradingCompetition(competitionCode) ? getWorldCupFlagCountryCode(position.team_code) : null;
            const { buyTrades, sellTrades, buySummary, sellSummary, pnlBreakdown } = buildPositionTradeColumns({
              positionTeamCode: position.team_code,
              trades: positionTradesByTeamCode.get(position.team_code.trim().toUpperCase()) ?? [],
              totalTradingPnl: position.position_realized_pnl + (position.unrealized_pnl ?? 0),
              realizedPnl: position.position_realized_pnl,
              unrealizedPnl: position.unrealized_pnl,
            });

            return (
              <article
                key={position.position_id}
                className={cn(
                  "positionMobileCard positionTabletCard rounded-xl border bg-background-card p-3 shadow-[0_12px_28px_rgba(0,0,0,0.28)] transition-colors md:p-4",
                  isExpanded ? "border-brand/85" : "border-border-subtle"
                )}
              >
                <button
                  type="button"
                  onClick={() => toggleExpandedPosition(position.position_id)}
                  aria-expanded={isExpanded}
                  className="grid min-h-11 w-full grid-cols-[auto_minmax(0,1fr)] items-start gap-3 text-left md:grid-cols-[auto_minmax(16rem,1.8fr)_minmax(4.2rem,0.46fr)_minmax(3.6rem,0.34fr)_minmax(4.2rem,0.46fr)_minmax(5.2rem,0.54fr)_auto] md:items-center md:gap-3"
                >
                  <div className="self-start">
                    <TeamCodeBadge
                      code={position.team_code}
                      size={TEAM_IDENTITY_BADGE_SIZE.portfolio.cardBadge}
                      primaryColor={position.team_primary_color ?? null}
                      secondaryColor={position.team_secondary_color ?? null}
                      variant={worldCupFlagCountryCode ? "worldCupFlag" : undefined}
                      flagCountryCode={worldCupFlagCountryCode}
                    />
                  </div>

                  <div className="min-w-0">
                    <p className={TEAM_IDENTITY_NAME_CLASS.portfolioCard}>
                      {teamName}
                    </p>
                    <span
                      className={cn(
                        "positionTabletSidePill mt-1 hidden h-[22px] w-fit min-w-12 items-center justify-center rounded px-2 font-display text-[9px] font-bold uppercase tracking-[0.06em] md:inline-flex",
                        sideTone,
                      )}
                    >
                      {sideLabel}
                    </span>
                    <div className="mt-1.5 grid grid-cols-[auto_minmax(2.2rem,0.75fr)_minmax(2.8rem,0.9fr)_minmax(3.2rem,1fr)_auto] items-end gap-x-2 gap-y-1 md:hidden">
                      <span
                        className={cn(
                          "positionMobileSidePill inline-flex h-[22px] min-w-12 items-center justify-center self-center rounded px-2 font-display text-[9px] font-bold uppercase tracking-[0.06em]",
                          sideTone,
                        )}
                      >
                        {sideLabel}
                      </span>
                      <MobilePositionMetric
                        label={t("portfolio.metric.qty")}
                        value={quantityFormatter.format(Math.abs(position.quantity))}
                      />
                      <MobilePositionMetric
                        label={t("portfolio.metric.mark")}
                        value={markFormatter.format(position.current_score)}
                      />
                      <MobilePositionMetric
                        align="right"
                        label={t("portfolio.metric.pnl")}
                        value={pnl.value}
                        valueClassName={pnl.tone}
                      />
                      <span className="pb-1 font-display text-lg leading-none text-foreground-muted md:flex md:min-h-11 md:w-11 md:items-center md:justify-center md:justify-self-end md:pb-0" aria-hidden="true">
                        {isExpanded ? "▴" : "▾"}
                      </span>
                    </div>
                  </div>
                  <MobilePositionMetric
                    className="hidden md:block"
                    label={t("portfolio.metric.entry")}
                    value={entryValue != null ? entryFormatter.format(entryValue) : "—"}
                  />
                  <MobilePositionMetric
                    className="hidden md:block"
                    label={t("portfolio.metric.qty")}
                    value={quantityFormatter.format(Math.abs(position.quantity))}
                  />
                  <MobilePositionMetric
                    className="hidden md:block"
                    label={t("portfolio.metric.mark")}
                    value={markFormatter.format(position.current_score)}
                  />
                  <MobilePositionMetric
                    className="hidden md:block"
                    align="right"
                    label={t("portfolio.metric.pnl")}
                    value={pnl.value}
                    valueClassName={pnl.tone}
                  />
                  <span className="hidden font-display text-lg leading-none text-foreground-muted md:flex md:min-h-11 md:w-8 md:items-center md:justify-center md:justify-self-end" aria-hidden="true">
                    {isExpanded ? "▴" : "▾"}
                  </span>
                </button>
                {isExpanded && (
                  <div className="mt-3 border-t border-white/[0.08] pt-3">
                    <PositionTradeDetails
                      variant="mobileCard"
                      buyTrades={buyTrades}
                      sellTrades={sellTrades}
                      buySummary={buySummary}
                      sellSummary={sellSummary}
                      pnlBreakdown={pnlBreakdown}
                      numberFormatter={numberFormatter}
                      entryFormatter={entryFormatter}
                    />
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </div>
    </div>
  );
}

type PositionTradeSummary = {
  totalQuantity: number;
  averagePrice: number | null;
};

type PositionPnlBreakdown = {
  realizedPnl: number;
  unrealizedPnl: number | null;
};

function MobilePositionMetric({
  label,
  value,
  valueClassName,
  align = "left",
  className,
}: {
  label: string;
  value: string;
  valueClassName?: string;
  align?: "left" | "right";
  className?: string;
}) {
  return (
    <div className={cn("min-w-0", align === "right" && "text-right", className)}>
      <p className="truncate font-display text-[10px] font-bold uppercase leading-none tracking-[0.08em] text-foreground-faint">
        {label}
      </p>
      <p
        className={cn(
          "mt-0.5 truncate font-display text-[17px] font-bold leading-none text-foreground tabular-nums",
          valueClassName,
        )}
      >
        {value}
      </p>
    </div>
  );
}

function PositionTradeDetails({
  variant = "default",
  buyTrades,
  sellTrades,
  buySummary,
  sellSummary,
  pnlBreakdown,
  numberFormatter,
  entryFormatter,
}: {
  buyTrades: PortfolioTrade[];
  sellTrades: PortfolioTrade[];
  buySummary: PositionTradeSummary;
  sellSummary: PositionTradeSummary;
  pnlBreakdown: PositionPnlBreakdown;
  numberFormatter: Intl.NumberFormat;
  entryFormatter: Intl.NumberFormat;
  variant?: "default" | "mobileCard";
}) {
  const { t } = useLocalization();
  const isMobileCard = variant === "mobileCard";
  const tradeLog = [...buyTrades, ...sellTrades].sort((left, right) => {
    const leftTime = left.executed_at ? new Date(left.executed_at).getTime() : 0;
    const rightTime = right.executed_at ? new Date(right.executed_at).getTime() : 0;
    if (leftTime !== rightTime) return rightTime - leftTime;
    return right.trade_id - left.trade_id;
  });

  return (
    <div
      className={cn(
        "positionTradePanelMobile",
        isMobileCard
          ? "bg-transparent p-0"
          : "rounded-lg border border-white/[0.08] bg-background-raised/35 p-3"
      )}
    >
      <div className={cn("grid gap-2", isMobileCard ? "grid-cols-4" : "sm:grid-cols-4")}>
        <PositionStatTile
          label={t("portfolio.position.buyAvg")}
          value={buySummary.averagePrice == null ? "—" : entryFormatter.format(buySummary.averagePrice)}
          className="text-success"
          variant={variant}
        />
        <PositionStatTile
          label={t("portfolio.position.sellAvg")}
          value={sellSummary.averagePrice == null ? "—" : entryFormatter.format(sellSummary.averagePrice)}
          className="text-danger"
          variant={variant}
        />
        <PositionStatTile
          label={t("portfolio.position.realized")}
          value={formatSignedPnl(pnlBreakdown.realizedPnl, numberFormatter)}
          className={pnlTone(pnlBreakdown.realizedPnl)}
          variant={variant}
        />
        <PositionStatTile
          label={t("portfolio.position.unrealized")}
          value={pnlBreakdown.unrealizedPnl == null ? "—" : formatSignedPnl(pnlBreakdown.unrealizedPnl, numberFormatter)}
          className={pnlTone(pnlBreakdown.unrealizedPnl ?? 0)}
          variant={variant}
        />
      </div>

      <p className="mt-4 font-display text-[13px] font-bold uppercase tracking-[0.12em] text-foreground-faint">
        {t("portfolio.position.tradeLog")}
      </p>
      {tradeLog.length > 0 ? (
        <div
          className={cn(
            "mt-2 divide-y divide-white/[0.08] overflow-hidden",
            isMobileCard ? "border-t border-white/[0.08]" : "rounded-md border border-white/[0.08]"
          )}
        >
          {tradeLog.map((trade) => (
            <PositionTradeLogRow key={`position-trade-log-${trade.trade_id}`} trade={trade} variant={variant} />
          ))}
        </div>
      ) : (
        <div className="mt-2 rounded-md border border-dashed border-white/[0.1] px-3 py-2 text-sm text-foreground-muted">
          {t("portfolio.empty.history")}
        </div>
      )}
    </div>
  );
}

function PositionStatTile({
  label,
  value,
  className,
  variant = "default",
}: {
  label: string;
  value: string;
  className?: string;
  variant?: "default" | "mobileCard";
}) {
  const isMobileCard = variant === "mobileCard";

  return (
    <div className={cn(isMobileCard ? "min-w-0 py-1" : "rounded-md border border-white/[0.08] bg-background-card/70 px-3 py-2")}>
      <p
        className={cn(
          "truncate font-display font-bold uppercase tracking-[0.12em] text-foreground-faint",
          isMobileCard ? "text-[11px] leading-none" : "text-[10px]",
        )}
      >
        {label}
      </p>
      <p
        className={cn(
          "mt-1 truncate font-display font-bold leading-none tabular-nums text-foreground",
          isMobileCard ? "text-[17px]" : "text-sm",
          className,
        )}
      >
        {value}
      </p>
    </div>
  );
}

function PositionTradeLogRow({
  trade,
  variant = "default",
}: {
  trade: PortfolioTrade;
  variant?: "default" | "mobileCard";
}) {
  const { t } = useLocalization();
  const sideClass = trade.side === "BUY" ? "text-success" : "text-danger";
  const isMobileCard = variant === "mobileCard";

  return (
    <div
      className={cn(
        "grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-2 text-sm",
        isMobileCard ? "bg-transparent py-3" : "bg-background-card/60 px-3 py-2",
      )}
    >
      <div className="flex min-w-0 items-center gap-2">
        <span className={cn("inline-block min-w-[42px] font-display text-[14px] font-bold uppercase tracking-[0.06em]", sideClass)}>
          {t(trade.side === "BUY" ? "portfolio.filters.buy" : "portfolio.filters.sell").toUpperCase()}
        </span>
        <span className="font-display text-[15px] font-bold text-foreground">
          {formatTradePrice(trade.price)} x {trade.quantity}
        </span>
      </div>
      <span className="min-w-0 truncate text-right font-display text-[14px] font-bold text-foreground">
        {getCounterpartyDisplayName(trade)}
      </span>
    </div>
  );
}

function formatTradePrice(price: number) {
  return Number.isInteger(price) ? price.toLocaleString() : price.toFixed(1);
}

function formatSignedPnl(value: number, formatter: Intl.NumberFormat) {
  const formatted = formatter.format(Math.abs(value));
  return value >= 0 ? `+${formatted}` : `-${formatted}`;
}

function pnlTone(value: number) {
  if (Math.abs(value) < 0.5) return "text-foreground-muted";
  return value > 0 ? "text-success" : "text-danger";
}

function getCounterpartyDisplayName(trade: PortfolioTrade) {
  const displayName = trade.counterparty_display_name?.trim();
  if (!trade.counterparty_trader_code && displayName?.toLowerCase() === "deleted trader") {
    return "DELETED";
  }
  return trade.counterparty_trader_code ?? displayName ?? "—";
}

function formatPnl(
  unrealizedPnl: number | null | undefined,
  numberFormatter: Intl.NumberFormat,
): { value: string; tone: string } {
  if (unrealizedPnl == null || Math.abs(unrealizedPnl) < 0.5) {
    return { value: "0", tone: "text-foreground-muted" };
  }

  return {
    value: `${unrealizedPnl > 0 ? "+" : ""}${numberFormatter.format(unrealizedPnl)}`,
    tone: unrealizedPnl > 0 ? "text-success" : "text-danger",
  };
}

function isFlatPosition(position: PortfolioPosition) {
  return position.quantity === 0 || position.status === "FLAT";
}

function isShortPosition(position: PortfolioPosition) {
  return !isFlatPosition(position) && position.quantity < 0;
}

function getPositionSideLabel(position: PortfolioPosition, t: ReturnType<typeof useLocalization>["t"]) {
  if (isFlatPosition(position)) return t("portfolio.position.flat");
  return isShortPosition(position)
    ? t("portfolio.position.short")
    : t("portfolio.position.long");
}

function getPositionSideTone(position: PortfolioPosition) {
  if (isFlatPosition(position)) {
    return "border border-border-subtle bg-background-raised text-foreground-muted";
  }
  return isShortPosition(position) ? "bg-danger/20 text-danger" : "bg-success/20 text-success";
}

function getPositionTradingPnl(position: PortfolioPosition) {
  return position.position_realized_pnl + (position.unrealized_pnl ?? 0);
}

export function usePositionsHowTo() {
  const [showHowTo, setShowHowTo] = useState(false);
  return {
    showHowTo,
    openHowTo: () => setShowHowTo(true),
    closeHowTo: () => setShowHowTo(false),
  };
}
